import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { supabase } from '../supabaseClient';
import Footer from '../components/Footer';
import { CONTACT_EMAIL, LISTING_TYPES, SHIPPING_NOTICE } from '../constants/gaming';

const Particles = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth; canvas.height = window.innerHeight;
    const count = window.innerWidth < 768 ? 50 : 120;
    let pts = Array.from({ length: count }, () => ({
      x: Math.random()*canvas.width, y: Math.random()*canvas.height,
      sz: Math.random()*2+0.5, vx: Math.random()*1.4-0.7, vy: Math.random()*1.4-0.7,
      color: `hsl(${Math.random()*60+200},100%,55%)`, a: Math.random()*0.5+0.1,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0,0,canvas.width,canvas.height);
      pts.forEach((p,i) => {
        p.x+=p.vx; p.y+=p.vy;
        if(p.x<0||p.x>canvas.width) p.vx*=-1;
        if(p.y<0||p.y>canvas.height) p.vy*=-1;
        for(let j=i+1;j<pts.length;j++){
          const dx=p.x-pts[j].x,dy=p.y-pts[j].y,d=Math.sqrt(dx*dx+dy*dy);
          if(d<90){ctx.strokeStyle=`rgba(0,212,255,${(1-d/90)*0.45})`;ctx.lineWidth=0.4;ctx.beginPath();ctx.moveTo(p.x,p.y);ctx.lineTo(pts[j].x,pts[j].y);ctx.stroke();}
        }
        ctx.save();ctx.globalAlpha=p.a;ctx.fillStyle=p.color;ctx.beginPath();ctx.arc(p.x,p.y,p.sz,0,Math.PI*2);ctx.fill();ctx.restore();
      });
      raf=requestAnimationFrame(draw);
    };
    draw();
    const resize=()=>{canvas.width=window.innerWidth;canvas.height=window.innerHeight;};
    window.addEventListener('resize',resize);
    return()=>{cancelAnimationFrame(raf);window.removeEventListener('resize',resize);};
  },[]);
  return <Canvas ref={ref}/>;
};

const Canvas = styled.canvas`position:fixed;top:0;left:0;width:100%;height:100%;z-index:0;pointer-events:none;`;
const neonGlow=keyframes`0%,100%{text-shadow:0 0 10px #ff00ff,0 0 22px #ff00ff,0 0 40px #ff00ff;}50%{text-shadow:0 0 6px #ff00ff,0 0 12px #ff00ff;}`;
const scanline=keyframes`0%{background-position:0 0;}100%{background-position:0 100vh;}`;
const cardIn=keyframes`from{opacity:0;transform:translateY(20px) scale(0.97);}to{opacity:1;transform:none;}`;
const shimmer=keyframes`0%{background-position:-200% center;}100%{background-position:200% center;}`;
const borderPulse=keyframes`0%,100%{box-shadow:0 0 0 0 rgba(0,212,255,0);}50%{box-shadow:0 0 18px 2px rgba(0,212,255,0.22);}`;

const Wrap=styled.div`min-height:100vh;background:linear-gradient(135deg,#080820,#111138,#1a1a50);overflow-x:hidden;position:relative;color:white;font-family:'Arial',sans-serif;`;
const Scanlines=styled.div`position:fixed;top:0;left:0;width:100%;height:100%;background:linear-gradient(to bottom,transparent 0%,rgba(255,255,255,0.02) 50%,transparent 100%);background-size:100% 7px;animation:${scanline} 4s linear infinite;pointer-events:none;z-index:1;`;
const PageContent=styled.div`position:relative;z-index:2;padding-bottom:60px;`;
const Hero=styled.div`text-align:center;padding:80px 20px 56px;h1{font-size:3.2rem;color:#ff00ff;letter-spacing:4px;font-weight:900;margin:0 0 12px;animation:${neonGlow} 1.8s ease-in-out infinite alternate;@media(max-width:600px){font-size:2rem;}}p{font-size:0.9rem;color:rgba(255,255,255,0.45);letter-spacing:3px;text-transform:uppercase;}`;
const ShippingNotice=styled.div`
  display:inline-block;box-sizing:border-box;
  margin:18px auto 0;
  padding:10px 24px;
  font-size:1.05rem;
  font-weight:700;
  letter-spacing:0.3px;
  color:#00d4ff;
  background:linear-gradient(135deg,rgba(0,212,255,0.14),rgba(255,0,255,0.12));
  border:1px solid rgba(0,212,255,0.35);
  border-radius:999px;
  max-width:100%;
  white-space:nowrap;
  @media(max-width:640px){
    white-space:normal;
    line-height:1.3;
    padding:8px 16px;
    font-size:0.92rem;
    max-width:94vw;
  }
`;
const Section=styled.section`max-width:1200px;margin:0 auto;padding:0 28px;`;
const SectionLabel=styled.div`font-size:0.72rem;letter-spacing:3px;text-transform:uppercase;color:#00d4ff;margin-bottom:28px;padding-bottom:12px;border-bottom:1px solid rgba(0,212,255,0.18);display:flex;align-items:center;gap:10px;&::before{content:'';display:inline-block;width:4px;height:18px;background:linear-gradient(#00d4ff,#ff00ff);border-radius:2px;}`;
const Grid=styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin-bottom:60px;@media(max-width:1024px){grid-template-columns:repeat(2,1fr);gap:18px;}@media(max-width:580px){grid-template-columns:1fr;gap:14px;}`;

const Card=styled.div`
  display:flex;flex-direction:column;
  background:rgba(8,8,32,0.88);border-radius:14px;overflow:hidden;
  border:1px solid rgba(0,212,255,0.15);backdrop-filter:blur(10px);
  transition:all 0.35s cubic-bezier(0.22,1,0.36,1);position:relative;
  animation:${cardIn} 0.5s ease both;animation-delay:${p=>p.$i*0.07}s;
  &:hover{transform:translateY(-10px) scale(1.03);border-color:rgba(255,0,255,0.5);
           box-shadow:0 20px 50px rgba(255,0,255,0.2),0 0 0 1px rgba(255,0,255,0.22);}
  &:hover .ov{opacity:1;} &:hover img{transform:scale(1.08);}
`;
const ImgWrap=styled.div`width:100%;aspect-ratio:16/10;overflow:hidden;position:relative;background:linear-gradient(135deg,#0a0a28,#180a30);flex-shrink:0;`;
const CardImg=styled.img`width:100%;height:100%;object-fit:cover;transition:transform 0.5s ease;`;
const ImgPlaceholder=styled.div`width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:3rem;color:rgba(255,255,255,0.1);`;
const Overlay=styled.div`position:absolute;inset:0;background:linear-gradient(to bottom,rgba(255,0,255,0.12),rgba(0,0,0,0.45));opacity:0;transition:opacity 0.3s;`;
const Body=styled.div`display:flex;flex-direction:column;flex:1;padding:16px 18px 20px;`;
const CardTitle=styled.h3`color:#fff;font-size:0.95rem;font-weight:700;margin:0 0 8px;line-height:1.3;`;
const CardDesc=styled.p`color:rgba(255,255,255,0.45);font-size:0.8rem;line-height:1.55;margin:0 0 14px;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;`;
const CtaLink=styled.a`
  display:inline-flex;align-items:center;gap:5px;padding:8px 18px;
  margin-top:auto;align-self:flex-start;
  background:${p=>p.$variant==='local'?'linear-gradient(135deg,rgba(255,183,0,0.14),rgba(255,0,255,0.1))':'linear-gradient(135deg,rgba(0,212,255,0.12),rgba(255,0,255,0.12))'};
  border:1px solid ${p=>p.$variant==='local'?'rgba(255,183,0,0.32)':'rgba(0,212,255,0.28)'};
  color:${p=>p.$variant==='local'?'#ffb700':'#00d4ff'};
  text-decoration:none;font-size:0.75rem;font-weight:700;letter-spacing:1px;text-transform:uppercase;
  border-radius:8px;transition:all 0.25s ease;animation:${borderPulse} 3s ease infinite;
  &:hover{background:${p=>p.$variant==='local'?'linear-gradient(135deg,rgba(255,183,0,0.28),rgba(255,0,255,0.2))':'linear-gradient(135deg,rgba(0,212,255,0.28),rgba(255,0,255,0.22))'};color:#fff;transform:translateY(-2px);}
  &::after{content:'${p=>p.$variant==='local'?'✉':'↗'}';}
`;
const ShowcaseBadge=styled.div`
  display:inline-flex;align-items:center;gap:6px;padding:8px 18px;
  margin-top:auto;align-self:flex-start;
  background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.15);
  color:rgba(255,255,255,0.4);font-size:0.75rem;font-weight:700;letter-spacing:1px;text-transform:uppercase;
  border-radius:8px;cursor:default;
`;
const EmptyState=styled.div`text-align:center;padding:80px 20px;color:rgba(255,255,255,0.25);.ico{font-size:4rem;display:block;margin-bottom:14px;}h3{color:rgba(255,255,255,0.4);font-size:1.1rem;margin-bottom:6px;}p{font-size:0.82rem;}`;

const buildMailtoHref=(title)=>{
  const subject=encodeURIComponent(`Inquiry about ${title} - ModMedia Gaming`);
  const body=encodeURIComponent(`Hi ModMedia, I am interested in the "${title}" listing on ModMedianow.com/gaming. Is it still available?`);
  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
};
const SkeletonCard=styled.div`border-radius:14px;overflow:hidden;border:1px solid rgba(0,212,255,0.08);background:rgba(8,8,32,0.7);.s-img{width:100%;aspect-ratio:16/10;background:linear-gradient(90deg,rgba(255,255,255,0.03) 25%,rgba(255,255,255,0.07) 50%,rgba(255,255,255,0.03) 75%);background-size:200% 100%;animation:${shimmer} 1.6s infinite;}.s-body{padding:16px 18px;}.s-line{height:11px;border-radius:5px;background:rgba(255,255,255,0.05);margin-bottom:9px;animation:${shimmer} 1.6s infinite;background-size:200% 100%;}&.w60{width:60%;}&.w85{width:85%;}`;

const Gaming = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('gaming_cards').select('*').order('sort_order',{ascending:true})
      .then(({data})=>{setCards(data||[]);setLoading(false);});
  }, []);

  return (
    <Wrap>
      <Particles />
      <Scanlines />
      <PageContent>
        <Hero>
          <h1>🎮 ModMedia Gaming</h1>
          <p>Curated gaming picks &amp; marketplace</p>
          <p>(Our Email Has Changed! New: modmedianow@gmail.com )</p>
          <ShippingNotice>{SHIPPING_NOTICE}</ShippingNotice>
        </Hero>
        <Section>
          <SectionLabel>Featured Games &amp; Listings</SectionLabel>
          {loading ? (
            <Grid>
              {[1,2,3,4,5].map(i=>(
                <SkeletonCard key={i}>
                  <div className="s-img"/>
                  <div className="s-body">
                    <div className="s-line" style={{width:'85%'}}/><div className="s-line" style={{width:'60%'}}/><div className="s-line" style={{width:'85%'}}/>
                  </div>
                </SkeletonCard>
              ))}
            </Grid>
          ) : cards.length === 0 ? (
            <EmptyState>
              <span className="ico">🕹️</span>
              <h3>No cards yet</h3>
              <p>Add gaming cards from the Admin Dashboard.</p>
            </EmptyState>
          ) : (
            <Grid>
              {cards.map((card,i)=>(
                <Card key={card.id} $i={i}>
                  <ImgWrap>
                    {card.image_url
                      ? <CardImg src={card.image_url} alt={card.title} loading="lazy"/>
                      : <ImgPlaceholder>🎮</ImgPlaceholder>}
                    <Overlay className="ov"/>
                  </ImgWrap>
                  <Body>
                    <CardTitle>{card.title}</CardTitle>
                    {card.description && <CardDesc>{card.description}</CardDesc>}
                    {card.listing_type===LISTING_TYPES.EBAY && card.external_link && (
                      <CtaLink $variant="ebay" href={card.external_link} target="_blank" rel="noopener noreferrer">
                        Buy Now - eBay
                      </CtaLink>
                    )}
                    {card.listing_type===LISTING_TYPES.LOCAL && (
                      <CtaLink $variant="local" href={buildMailtoHref(card.title)}>
                        Local Only - Contact
                      </CtaLink>
                    )}
                    {card.listing_type===LISTING_TYPES.SHOWCASE && (
                      <ShowcaseBadge>Showcase - Not For Sale</ShowcaseBadge>
                    )}
                  </Body>
                </Card>
              ))}
            </Grid>
          )}
        </Section>
      </PageContent>
      <Footer />
    </Wrap>
  );
};

export default Gaming;
